import { formatInTimeZone } from 'date-fns-tz';
import { backendClient } from '@/lib/api-client';
import {
  type ChronologyEventDetail,
  type ChronologyEventLight,
  fetchChronologyExport,
} from '@/hooks/useChronology';

export interface ExportSourceDocument {
  guid: string;
  filename: string;
  doc_date: string | null;
  summary: string;
}

interface ExportSourceEntry {
  label: string;
  url: string | null;
}

interface ChronologyExportOptions {
  matterGuid: string;
  chronologyId?: string | null;
  filteredEvents: ChronologyEventLight[];
  dateFormat: string;
  includeSources: boolean;
  includeDetail: boolean;
  includeSourceDocumentsTable: boolean;
  includeCrimsonDocumentLinks: boolean;
  onComplete?: () => void;
}

type ExportPrecision = NonNullable<ChronologyEventLight['start_precision']>;
type ExportTimePrecision = Extract<ExportPrecision, 'hour' | 'minute' | 'second'>;

async function fetchChronologyExportDocumentMetadata(
  matterGuid: string,
  documentGuids: string[],
): Promise<ExportSourceDocument[]> {
  if (!documentGuids.length) return [];
  const response = await backendClient.post(
    `/matters/${matterGuid}/documents/chronology-export-metadata`,
    { document_guids: documentGuids },
  );
  return response.data?.documents || [];
}

function exportMonthFormat(dateFormat: string): string {
  if (dateFormat === 'yyyy-MM-dd') return 'yyyy-MM';
  if (dateFormat.includes('MMM')) return 'MMM yyyy';
  return 'MM/yyyy';
}

function formatExportBound(
  date: Date,
  precision: ExportPrecision,
  dateFormat: string,
): string {
  if (precision === 'year') return formatInTimeZone(date, 'UTC', 'yyyy');
  if (precision === 'month') return formatInTimeZone(date, 'UTC', exportMonthFormat(dateFormat));

  let fullFormat = dateFormat;
  if (precision === 'hour') fullFormat += ' HH:00';
  if (precision === 'minute') fullFormat += ' HH:mm';
  if (precision === 'second') fullFormat += ' HH:mm:ss';
  return formatInTimeZone(date, 'UTC', fullFormat);
}

function formatExportTime(date: Date, precision: ExportTimePrecision): string {
  if (precision === 'hour') return formatInTimeZone(date, 'UTC', 'HH:00');
  if (precision === 'minute') return formatInTimeZone(date, 'UTC', 'HH:mm');
  return formatInTimeZone(date, 'UTC', 'HH:mm:ss');
}

function hasExportTimePrecision(precision: ExportPrecision): precision is ExportTimePrecision {
  return precision === 'hour' || precision === 'minute' || precision === 'second';
}

function isSameUtcDay(a: Date, b: Date): boolean {
  return (
    a.getUTCFullYear() === b.getUTCFullYear()
    && a.getUTCMonth() === b.getUTCMonth()
    && a.getUTCDate() === b.getUTCDate()
  );
}

function isEdtfRange(temporalExpression: string): boolean {
  return temporalExpression.includes('/') || temporalExpression.includes('..');
}

export function formatExportDate(ev: ChronologyEventLight, dateFormat: string, empty: string): string {
  const lower = ev.temporal_lower;
  const upper = ev.temporal_upper;
  const startPrecision = ev.start_precision;
  const endPrecision = ev.end_precision;
  const temporalExpression = ev.temporal_expression?.trim() ?? '';
  const prefix = temporalExpression.startsWith('~')
    ? '~'
    : temporalExpression.startsWith('?') ? '?' : '';

  if (ev.temporal_lower_inf && upper && endPrecision) {
    const inclusiveUpper = new Date(upper.getTime() - 1);
    return `Until ${prefix}${formatExportBound(inclusiveUpper, endPrecision, dateFormat)}`;
  }
  if (ev.temporal_upper_inf && lower && startPrecision) {
    return `Since ${prefix}${formatExportBound(lower, startPrecision, dateFormat)}`;
  }
  if (!lower || !startPrecision) {
    return ev.temporal_display || empty;
  }
  if (temporalExpression && !isEdtfRange(temporalExpression)) {
    return `${prefix}${formatExportBound(lower, startPrecision, dateFormat)}`;
  }
  if (!upper || !endPrecision) {
    return `${prefix}${formatExportBound(lower, startPrecision, dateFormat)}`;
  }

  const inclusiveUpper = new Date(upper.getTime() - 1);
  if (
    hasExportTimePrecision(startPrecision)
    && hasExportTimePrecision(endPrecision)
    && isSameUtcDay(lower, inclusiveUpper)
  ) {
    const date = formatInTimeZone(lower, 'UTC', dateFormat);
    const startTime = formatExportTime(lower, startPrecision);
    const endTime = formatExportTime(inclusiveUpper, endPrecision);
    if (startTime === endTime) return `${prefix}${date} ${startTime}`;
    return `${prefix}${date} ${startTime}-${endTime}`;
  }

  const start = formatExportBound(lower, startPrecision, dateFormat);
  const end = formatExportBound(inclusiveUpper, endPrecision, dateFormat);
  if (start === end) return `${prefix}${start}`;
  return `${prefix}${start} to ${end}`;
}

function formatExportDocumentDate(docDate: string | null, dateFormat: string, empty: string): string {
  if (!docDate) return empty;
  const parsed = new Date(docDate);
  if (Number.isNaN(parsed.getTime())) return docDate;
  return formatInTimeZone(parsed, 'UTC', dateFormat);
}

function buildCrimsonDocumentUrl(matterGuid: string, documentGuid: string | null | undefined): string | null {
  if (!documentGuid) return null;
  const origin = globalThis.location?.origin;
  if (!origin) return null;
  return `${origin}/matter/${matterGuid}/f/${documentGuid}`;
}

function getExportSourceLabel(doc: ChronologyEventDetail['documents'][number]): string {
  return doc.filename || doc.doc_type_label || doc.doc_type || 'Source document';
}

function buildExportSourceEntries(
  matterGuid: string,
  documents: ChronologyEventDetail['documents'],
): ExportSourceEntry[] {
  return documents.map(doc => ({
    label: getExportSourceLabel(doc),
    url: buildCrimsonDocumentUrl(matterGuid, doc.guid),
  }));
}

function collectExportDocumentGuids(events: ChronologyEventDetail[]): string[] {
  const seen = new Set<string>();
  const guids: string[] = [];
  for (const event of events) {
    for (const doc of event.documents) {
      if (!doc.guid || seen.has(doc.guid)) continue;
      seen.add(doc.guid);
      guids.push(doc.guid);
    }
  }
  return guids;
}

function sortExportSourceDocuments(a: ExportSourceDocument, b: ExportSourceDocument): number {
  const dateA = a.doc_date ?? '';
  const dateB = b.doc_date ?? '';
  if (dateA !== dateB) return dateA.localeCompare(dateB);
  return (a.filename || '').localeCompare(b.filename || '');
}

function applySheetColumnWidths(
  ws: Record<string, unknown>,
  rows: Record<string, string>[],
  headers: string[],
) {
  const maxColWidth = 80;
  ws['!cols'] = headers.map(header => {
    const headerLen = header.length;
    const maxBodyLen = rows.reduce((max, row) => {
      const cell = row[header] ?? '';
      const longestLine = cell.split('\n').reduce((lineMax, line) => Math.max(lineMax, line.length), 0);
      return Math.max(max, longestLine);
    }, 0);
    return { wch: Math.min(maxColWidth, Math.max(headerLen, maxBodyLen) + 2) };
  });
}

async function loadExportEvents({
  matterGuid,
  chronologyId = null,
  filteredEvents,
}: Pick<ChronologyExportOptions, 'matterGuid' | 'chronologyId' | 'filteredEvents'>): Promise<ChronologyEventDetail[]> {
  const selectedIds = new Set(filteredEvents.map(ev => ev.id));
  const { events: fullEvents } = await fetchChronologyExport(
    matterGuid,
    chronologyId ?? undefined,
  );
  const byId = new Map(fullEvents.map(ev => [ev.id, ev]));
  return filteredEvents
    .map(ev => byId.get(ev.id))
    .filter((ev): ev is ChronologyEventDetail => !!ev && selectedIds.has(ev.id));
}

async function loadExportSourceDocuments(
  matterGuid: string,
  exportEvents: ChronologyEventDetail[],
): Promise<ExportSourceDocument[]> {
  const documentGuids = collectExportDocumentGuids(exportEvents);
  const documents = await fetchChronologyExportDocumentMetadata(matterGuid, documentGuids);
  return documents.sort(sortExportSourceDocuments);
}

export async function downloadChronologyWord({
  matterGuid,
  chronologyId = null,
  filteredEvents,
  dateFormat,
  includeSources,
  includeDetail,
  includeSourceDocumentsTable,
  includeCrimsonDocumentLinks,
  onComplete,
}: ChronologyExportOptions): Promise<void> {
  const { Document, ExternalHyperlink, Paragraph, TextRun, Table, TableRow, TableCell, Packer, WidthType, BorderStyle, PageOrientation, TableLayoutType } = await import('docx');
  const fileSaver = await import('file-saver');
  const saveAs = fileSaver.default?.saveAs ?? fileSaver.saveAs ?? fileSaver.default;
  const exportEvents = await loadExportEvents({ matterGuid, chronologyId, filteredEvents });
  const exportSourceDocuments = includeSourceDocumentsTable
    ? await loadExportSourceDocuments(matterGuid, exportEvents)
    : [];

  const borderThin = { style: BorderStyle.SINGLE, size: 1, color: 'D1D5DB' };
  const cellBorders = { top: borderThin, bottom: borderThin, left: borderThin, right: borderThin };

  const totalWidth = 13958;
  const dateW = 1600;
  const sourcesW = includeSources ? 2800 : 0;
  const detailW = includeDetail ? (includeSources ? 4000 : 5600) : 0;
  const eventW = totalWidth - dateW - detailW - sourcesW;
  const colWidths = [dateW, eventW];
  if (includeDetail) colWidths.push(detailW);
  if (includeSources) colWidths.push(sourcesW);

  const makeCell = (text: string, header = false) => new TableCell({
    borders: cellBorders,
    ...(header && { shading: { fill: 'F1F5F9' } }),
    children: [new Paragraph({
      children: text.split('\n').map((line, index) =>
        new TextRun({ text: line, break: index > 0 ? 1 : undefined, bold: header, size: header ? 20 : 18, font: 'Calibri' }),
      ),
    })],
  });

  const makeSourcesCell = (entries: ExportSourceEntry[]) => new TableCell({
    borders: cellBorders,
    children: entries.length > 0
      ? entries.map((entry, index) => new Paragraph({
          spacing: { after: index < entries.length - 1 ? 120 : 0 },
          children: entry.url
            ? [new ExternalHyperlink({
                link: entry.url,
                children: [new TextRun({ text: entry.label, style: 'Hyperlink', size: 18, font: 'Calibri' })],
              })]
            : [new TextRun({ text: entry.label, size: 18, font: 'Calibri' })],
        }))
      : [new Paragraph({
          children: [new TextRun({ text: '', size: 18, font: 'Calibri' })],
        })],
  });

  const headerCells = [
    makeCell('Date', true),
    makeCell('Event', true),
  ];
  if (includeDetail) headerCells.push(makeCell('Detail', true));
  if (includeSources) headerCells.push(makeCell('Sources', true));

  const headerRow = new TableRow({ tableHeader: true, children: headerCells });

  const dataRows = exportEvents.map(ev => {
    const cells = [
      makeCell(formatExportDate(ev, dateFormat, '—')),
      makeCell(ev.description),
    ];
    if (includeDetail) cells.push(makeCell(ev.detail || ''));
    if (includeSources) {
      const sourceEntries = buildExportSourceEntries(matterGuid, ev.documents);
      const renderedSourceEntries = includeCrimsonDocumentLinks
        ? sourceEntries
        : sourceEntries.map(entry => ({ ...entry, url: null }));
      cells.push(makeSourcesCell(renderedSourceEntries));
    }
    return new TableRow({ children: cells });
  });

  const sourceDocumentHeaderRow = new TableRow({
    tableHeader: true,
    children: [
      makeCell('Document date', true),
      makeCell('File name', true),
      makeCell('Summary', true),
    ],
  });

  const sourceDocumentRows = exportSourceDocuments.map(doc => new TableRow({
    children: [
      makeCell(formatExportDocumentDate(doc.doc_date, dateFormat, '—')),
      makeCell(doc.filename || '—'),
      makeCell(doc.summary || '—'),
    ],
  }));

  const sourceDocumentChildren = includeSourceDocumentsTable
    ? [
        new Paragraph({ text: '' }),
        new Paragraph({
          children: [
            new TextRun({ text: 'Source documents', bold: true, size: 22, font: 'Calibri' }),
          ],
        }),
        new Table({
          width: { size: totalWidth, type: WidthType.DXA },
          layout: TableLayoutType.FIXED,
          columnWidths: [1800, 3600, totalWidth - 5400],
          rows: [sourceDocumentHeaderRow, ...sourceDocumentRows],
        }),
      ]
    : [];

  const doc = new Document({
    sections: [{
      properties: {
        page: {
          size: { orientation: PageOrientation.LANDSCAPE },
        },
      },
      children: [
        new Table({
          width: { size: totalWidth, type: WidthType.DXA },
          layout: TableLayoutType.FIXED,
          columnWidths: colWidths,
          rows: [headerRow, ...dataRows],
        }),
        ...sourceDocumentChildren,
      ],
    }],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, 'chronology.docx');
  onComplete?.();
}

export async function downloadChronologyExcel({
  matterGuid,
  chronologyId = null,
  filteredEvents,
  dateFormat,
  includeSources,
  includeDetail,
  includeSourceDocumentsTable,
  includeCrimsonDocumentLinks,
  onComplete,
}: ChronologyExportOptions): Promise<void> {
  const XLSX = await import('xlsx');
  const exportEvents = await loadExportEvents({ matterGuid, chronologyId, filteredEvents });
  const exportSourceDocuments = includeSourceDocumentsTable
    ? await loadExportSourceDocuments(matterGuid, exportEvents)
    : [];

  const rows = exportEvents.map(ev => {
    const row: Record<string, string> = {
      Date: formatExportDate(ev, dateFormat, ''),
      Event: ev.description,
    };
    if (includeDetail) row['Detail'] = ev.detail || '';
    if (includeSources) {
      row['Sources'] = ev.documents.map(doc => doc.filename || doc.doc_type_label || '').filter(Boolean).join('\n');
    }
    return row;
  });

  const ws = XLSX.utils.json_to_sheet(rows);
  const headers = Object.keys(rows[0] ?? { Date: '', Event: '' });
  applySheetColumnWidths(ws, rows, headers);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Chronology');
  if (includeSourceDocumentsTable) {
    const sourceDocumentRows = exportSourceDocuments.map(doc => ({
      'Document date': formatExportDocumentDate(doc.doc_date, dateFormat, ''),
      'File name': doc.filename || '',
      Summary: doc.summary || '',
    }));
    const sourceDocumentHeaders = ['Document date', 'File name', 'Summary'];
    const sourceWs = sourceDocumentRows.length > 0
      ? XLSX.utils.json_to_sheet(sourceDocumentRows)
      : XLSX.utils.aoa_to_sheet([sourceDocumentHeaders]);
    applySheetColumnWidths(sourceWs, sourceDocumentRows, sourceDocumentHeaders);
    XLSX.utils.book_append_sheet(wb, sourceWs, 'Source documents');
  }
  if (includeSources && includeCrimsonDocumentLinks) {
    const sourceLinkRows = exportEvents.flatMap(ev =>
      buildExportSourceEntries(matterGuid, ev.documents)
        .filter(entry => !!entry.url)
        .map(entry => ({
          Event: ev.description,
          Source: entry.label,
          'Crimson link': 'Open document',
          __url: entry.url as string,
        })),
    );
    const sourceLinkHeaders = ['Event', 'Source', 'Crimson link'];
    const sourceLinkSheetRows = sourceLinkRows.map(({ __url: _url, ...row }) => row);
    const sourceLinksWs = sourceLinkSheetRows.length > 0
      ? XLSX.utils.json_to_sheet(sourceLinkSheetRows)
      : XLSX.utils.aoa_to_sheet([sourceLinkHeaders]);
    for (let index = 0; index < sourceLinkRows.length; index += 1) {
      const cellRef = XLSX.utils.encode_cell({ r: index + 1, c: sourceLinkHeaders.indexOf('Crimson link') });
      const url = sourceLinkRows[index]?.__url;
      if (!url || !sourceLinksWs[cellRef]) continue;
      sourceLinksWs[cellRef].l = { Target: url };
    }
    applySheetColumnWidths(sourceLinksWs, sourceLinkSheetRows, sourceLinkHeaders);
    XLSX.utils.book_append_sheet(wb, sourceLinksWs, 'Source links');
  }
  XLSX.writeFile(wb, 'chronology.xlsx');
  onComplete?.();
}
